import React, { useCallback, useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import useKey from "utils/KeyListener";

import xmark from "imgs/xmark.png";

export default function Modal({ children, title, closeCb, disableEscape }) {
  const close = useCallback(() => {
    if (closeCb) closeCb();
  }, [closeCb]);

  useKey("Escape", false, (e) => {
    // Don't close if Escape is disabled (e.g., when TaskItems are in edit mode)
    // Also check if active element is within a TaskItem (showing all fields)
    // Or if any TaskItem has showAllFields set to true
    if (disableEscape || 
        document.activeElement?.closest('[data-task-id]') !== null ||
        document.querySelector('[data-task-id][data-show-all-fields="true"]') !== null) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    close();
  });

  const modalContentRef = useRef(null);
  const modalBackgroundRef = useRef(null);
  const [backgroundMinHeight, setBackgroundMinHeight] = useState(null);
  const initialPageHeightRef = useRef(null);

  // Calculate modal background height to be 80px taller than modal content when modal is larger than viewport
  // Also consider the Project page height if it's taller than the viewport
  useEffect(() => {
    // Capture the initial page height once when modal opens (before modal affects it)
    if (initialPageHeightRef.current === null) {
      const rootElement = document.getElementById('root');
      const viewportHeight = window.innerHeight;
      initialPageHeightRef.current = rootElement 
        ? Math.max(rootElement.scrollHeight, rootElement.offsetHeight, viewportHeight)
        : viewportHeight;
    }

    const updateBackgroundHeight = () => {
      if (!modalContentRef.current || !modalBackgroundRef.current) return;
      
      const contentHeight = modalContentRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // Use the initial page height (captured before modal opened) to avoid feedback loop
      const pageHeight = initialPageHeightRef.current || viewportHeight;
      
      if (contentHeight > pageHeight) {
        // Modal is larger than page, make background 80px taller than modal content
        setBackgroundMinHeight(contentHeight + 80);
      } else if (pageHeight > viewportHeight) {
        // Page is taller than viewport, make background cover the full page height
        setBackgroundMinHeight(pageHeight);
      } else {
        // Modal fits in viewport and page fits in viewport, use default (100vh)
        setBackgroundMinHeight(null);
      }
    };

    updateBackgroundHeight();
    window.addEventListener('resize', updateBackgroundHeight);
    
    // Use ResizeObserver to watch for content size changes only
    const resizeObserver = new ResizeObserver(updateBackgroundHeight);
    if (modalContentRef.current) {
      resizeObserver.observe(modalContentRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateBackgroundHeight);
      resizeObserver.disconnect();
      // Reset the initial page height ref when modal closes
      initialPageHeightRef.current = null;
    };
  }, [children]);

  const modalRoot = document.getElementById("modal-root");

  return ReactDOM.createPortal(
    <div
      ref={modalBackgroundRef}
      className="modal-background"
      onClick={() => {
        close();
      }}
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 50,
        minHeight: backgroundMinHeight || '100vh',
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          background: "#fff",
          padding: 30,
          position: "relative",
          width: 800,
          maxWidth: "90%",
          borderRadius: 14,
          display: "flex",
          flexDirection: "column",
          rowGap: 20,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div style={{ position: "absolute", right: -60, top: 0 }}>
          <img
            alt="closebtn"
            src={xmark}
            style={{
              position: "absolute",
              top: 20,
              right: 80,
              height: 10,
              filter: "invert(0.6)",
              cursor: "pointer",
              zIndex: 10,
            }}
            onClick={() => close()}
          />
        </div>
        {title && <h2 style={{ color: "#000", marginTop: -11 }}>{title}</h2>}

        {children}
      </div>
    </div>,
    modalRoot
  );
}
